import { useEffect } from 'react';

import api from '../api';

export const ACTIVE_PRESENCE_HEARTBEAT_INTERVAL_MS = 30 * 1000;
const PRESENCE_SESSION_STORAGE_KEY = 'allVoicedPresenceSessionId';

const createRandomSessionId = () => {
  if (typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  return `presence-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const getPresenceSessionId = () => {
  const existing = sessionStorage.getItem(
    PRESENCE_SESSION_STORAGE_KEY
  );

  if (existing) return existing;

  const sessionId = createRandomSessionId();
  sessionStorage.setItem(PRESENCE_SESSION_STORAGE_KEY, sessionId);

  return sessionId;
};

export default function useActiveUserPresenceHeartbeat({
  enabled,
  currentRoute,
}) {
  useEffect(() => {
    if (!enabled) return undefined;

    const sessionId = getPresenceSessionId();
    const sendHeartbeat = () => {
      Promise.resolve(
        api.post('/presence/heartbeat', {
          sessionId,
          currentRoute,
        })
      ).catch(() => {
          // Presence is best-effort and must not interrupt the app experience.
        });
    };

    let intervalId = null;

    const stopHeartbeatInterval = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };

    const startVisibleHeartbeat = () => {
      stopHeartbeatInterval();

      if (document.visibilityState !== 'visible') {
        return;
      }

      sendHeartbeat();
      intervalId = window.setInterval(
        sendHeartbeat,
        ACTIVE_PRESENCE_HEARTBEAT_INTERVAL_MS
      );
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startVisibleHeartbeat();
      } else {
        stopHeartbeatInterval();
      }
    };

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );
    startVisibleHeartbeat();

    return () => {
      stopHeartbeatInterval();
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );
    };
  }, [currentRoute, enabled]);
}
